import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Extensiones que el backend real acepta (decide el formato por la FIRMA de los bytes, no por la
 *  extensión — esto solo filtra lo obvio antes de subir, mismo criterio que el modal anterior). */
const EXTENSIONES = ['.xlsx', '.xls', '.csv'];

interface Props {
  onBack: () => void;
  onFileSelected: (file: File) => void;
}

/** Paso 2 del asistente: selección/drag-and-drop del archivo. El parseo real es del backend
 *  (ClosedXML/CSV) — no hay parseo local como en `bulkUpload/`, así que aquí solo se elige el
 *  archivo; la validación de contenido ocurre en el paso 3 (preview). */
export function StepUploadFile({ onBack, onFileSelected }: Readonly<Props>) {
  const { t } = useTranslation('recognition');
  const fileRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = (file: File) => {
    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!EXTENSIONES.includes(extension)) {
      setError(t('admin.winners.badExtension', { ext: EXTENSIONES.join(',') }));
      return;
    }
    setError(null);
    onFileSelected(file);
  };

  return (
    <div className="bulk-step bulk-step--upload">
      <button
        type="button"
        className={`bulk-dropzone ${isDragOver ? 'bulk-dropzone--over' : ''}`}
        onClick={() => fileRef.current?.click()}
        onDragOver={(event) => { event.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragOver(false);
          const file = event.dataTransfer.files?.[0];
          if (file) handleFile(file);
        }}
      >
        <i className="fas fa-file-excel" />
        <p>{t('admin.winners.importWizard.dropHint')}</p>
      </button>
      <label className="adm-sr-only" htmlFor="gan-wizard-archivo">
        {t('admin.winners.uploadFile')}
      </label>
      <input
        id="gan-wizard-archivo"
        ref={fileRef}
        type="file"
        accept={EXTENSIONES.join(',')}
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) handleFile(file);
          event.target.value = '';
        }}
      />

      {error && <p className="rp-error" role="alert"><i className="fas fa-triangle-exclamation" /> {error}</p>}

      <div className="bulk-wizard-nav">
        <button type="button" className="btn btn--secondary" onClick={onBack}>
          <i className="fas fa-arrow-left" /> {t('bulk.back')}
        </button>
        <button type="button" className="btn btn--secondary" onClick={() => fileRef.current?.click()}>
          <i className="fas fa-upload" /> {t('admin.winners.uploadFile')}
        </button>
      </div>
    </div>
  );
}

export default StepUploadFile;
